import { Icon } from "@iconify/react/dist/iconify.js";
import { Button } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useGetData from "../../api/useGetData";
import BookingLine from "./BookingLine";

/**
 * @typedef {Object} Booking
 * @property {name} string
 * @property {status} string
   @property {type} string    
   @property {user} string
   @property {arrival_time} string
   @property {arrival_date} string
   @property {booking_hours} string
   @property {vehicle_type} string
   @property {price} string
 */

/**
 * A component that renders the user's booking. It shows the booking data and enables the user to download a PDF receipt of that booking
 *
 * @param {Object} props
 * @param {Booking} booking - The booking data
 * @returns {JSX.Element}
 */

const BookingCard = ({ booking }) => {
  const [t] = useTranslation("global");
  const [errorMessage, setErrorMessage] = useState(null);
  const bookingClasses = {
    Pending: "tabler:clock-hour-3-filled",
    Booked: "tabler:clock-hour-3-filled",
    Completed: "tabler:circle-check-filled",
    "Non-Payment Cancellation": "tabler:circle-x-filled",
    Approved: "tabler:circle-check-filled",
  };
  const { getData } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_pdf_receipt",
    notLoadData: true,
    responseType: "arraybuffer",
    headers: {
      "accept-language": "en",
    },
  });
  const PdfDownload = async () => {
    const res = await getData({
      params: {
        name: booking.name,
      },
    });
    if (res.data) {
      const blob = new Blob([res.data], { type: "application/pdf" });
      // Create a link element
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${booking.name}.pdf`; // Set the file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setErrorMessage("my_booking.no_record_found");
    }
  };

  return (
    <div className="bg-text_white rounded-md px-5 py-7 flex flex-col justify-center items-center mb-4">
      <div className="p-4 bg-background_grey rounded-full">
        <Icon
          icon={bookingClasses[booking.status]}
          className="text-text_steel_blue"
          width="50"
          height="50"
        />
      </div>
      <p className="text-text_black font-semibold text-lg border-dashed border-b w-full text-center my-3 pb-4">
        {booking.status !== "Non-Payment Cancellation"
          ? t(`my_booking.${booking.status}`)
          : t("my_booking.cancelled")}
      </p>
      <BookingLine
        label={t("my_booking.booking_id_text")}
        value={booking.name}
      />
      <BookingLine
        label={t("my_booking.ride_type_text")}
        value={booking.type}
      />
      <BookingLine
        label={t("my_booking.rider_email_text")}
        value={booking.user}
      />
      <BookingLine
        label={t("my_booking.time_text")}
        value={booking.arrival_time}
      />
      <BookingLine
        label={t("my_booking.date_text")}
        value={booking.arrival_date}
      />
      <BookingLine
        label={t("my_booking.vehicle_booking_hours_text")}
        value={booking.booking_hours}
      />
      <BookingLine
        label={t("my_booking.vehicle_type_text")}
        value={booking.vehicle_type}
        className="border-b border-dashed pb-4"
      />
      <BookingLine
        label={t("my_booking.booking_price_text")}
        value={booking.price}
        className="border-b pb-4"
      />
      <Button
        onClick={() => PdfDownload()}
        type="button"
        className="bg-text_white w-full hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2 mb-2 mt-3 border border-text_black"
      >
        {t("my_booking.get_pdf_text")}
      </Button>
      <p className="text-text_warning capitalize">{t(errorMessage)}</p>
    </div>
  );
};

export default BookingCard;
